import { ApolloClient, from, HttpLink, split } from '@apollo/client'
import type { TypePolicies } from '@apollo/client/cache'
import { InMemoryCache } from '@apollo/client/cache'
import { RetryLink } from '@apollo/client/link/retry'
import { GraphQLWsLink } from '@apollo/client/link/subscriptions'
import { getMainDefinition, mergeDeep } from '@apollo/client/utilities'
import {
  fetchAuthSession,
  getJwtString,
} from '@backoffice-frontend/authentication'
import {
  fetchWithAuthHeaders,
  GraphQLEndpoint,
  SubscriptionGatewayEndpoint,
} from '@backoffice-frontend/common'
import { apolloVersion } from '@backoffice-frontend/environment'
import { FleetMaintenanceStore } from '@backoffice-frontend/fleet-maintenance'
import { introspectionResult } from '@backoffice-frontend/graphql'
import { MoiaHeaderSearchTypePolicies } from '@backoffice-frontend/patterns'
import {
  MoiaRegionTypePolicies,
  MoiaSelectedHubTypePolicies,
  MoiaServiceAreaTypePolicies,
} from '@backoffice-frontend/service-area-picker'
import { StopNetworkStore } from '@backoffice-frontend/stop-network'
import { createClient } from 'graphql-ws'

export type WebSocketEvent =
  | { type: 'connected' }
  | { type: 'closed' }
  | { type: 'error' }
  | { type: 'jwt_failed' }
  | { type: 'jwt_refresh_failed' }

type WebSocketEventListener = (event: WebSocketEvent) => void

let wsEventListener: WebSocketEventListener | undefined

export const setWebSocketEventListener = (
  listener: WebSocketEventListener | undefined,
) => {
  wsEventListener = listener
}

export const getApolloClient = (appName: string, policies: TypePolicies[]) => {
  const TypePolicies = {
    ...mergeDeep(
      MoiaHeaderSearchTypePolicies,
      MoiaServiceAreaTypePolicies,
      MoiaRegionTypePolicies,
      MoiaSelectedHubTypePolicies,
      StopNetworkStore,
      FleetMaintenanceStore,
      ...policies,
    ),
    ShiftState: {
      keyFields: ['shiftId'],
    },
    EmployeeNote: {
      keyFields: ['noteId', 'employeeId'],
    },
    ServiceArea: {
      fields: {
        locationAttributes: {
          merge: true,
        },
      },
    },
    Vehicle: {
      fields: {
        latestLocation: {
          merge: true,
        },
        fccCurrentAdAlerts: {
          merge: false,
        },
        fccUpcomingWaypoints: {
          merge: false,
        },
      },
    },

    FleetCalendarVehiclePlan: {
      keyFields: false as const,
      fields: {
        schedule: { merge: false },
      },
    },
  }

  const retryLink = new RetryLink({
    delay: {
      initial: 300,
      max: 5000,
      jitter: true,
    },
    attempts: {
      max: 3,
      retryIf: error => !!error,
    },
  })

  const httpLink = new HttpLink({
    uri: GraphQLEndpoint,
    fetch: (url: URL | RequestInfo, options?: RequestInit) =>
      fetchWithAuthHeaders(url.toString(), {
        ...options,
        addTraceIdHeader: true,
      }),
  })

  const wsClient = createClient({
    url: SubscriptionGatewayEndpoint,
    connectionParams: async () => {
      try {
        const token = await getJwtString()
        if (token) {
          const payload = JSON.parse(atob(token.split('.')[1]))
          const expiresAt = new Date(payload.exp * 1000)
          const remainingSeconds = Math.round(
            (expiresAt.getTime() - Date.now()) / 1000,
          )
          console.info(
            `[WebSocket] Token obtained, expires in ${remainingSeconds}s (at ${expiresAt.toISOString()})`,
          )
        } else {
          console.warn('[WebSocket] No token returned from session')
        }
        return { authorization: token }
      } catch (error) {
        console.error('[WebSocket] Failed to get token', error)
        wsEventListener?.({ type: 'jwt_failed' })
        try {
          console.info('[WebSocket] Attempting forced session refresh')
          const session = await fetchAuthSession({ forceRefresh: true })
          const token = session.tokens?.idToken?.toString()
          if (token) {
            const payload = JSON.parse(atob(token.split('.')[1]))
            const expiresAt = new Date(payload.exp * 1000)
            const remainingSeconds = Math.round(
              (expiresAt.getTime() - Date.now()) / 1000,
            )
            console.info(
              `[WebSocket] Refreshed token obtained, expires in ${remainingSeconds}s (at ${expiresAt.toISOString()})`,
            )
          } else {
            console.warn('[WebSocket] No token returned after forced refresh')
          }
          return { authorization: token }
        } catch (refreshError) {
          console.error('[WebSocket] Session refresh failed', refreshError)
          wsEventListener?.({ type: 'jwt_refresh_failed' })
          return {}
        }
      }
    },
    retryAttempts: 10,
    shouldRetry: () => true,
    retryWait: async (retries: number) => {
      const baseDelay = Math.min(1000 * 2 ** retries, 5000)
      const jitter = Math.random() * 300
      await new Promise(resolve => setTimeout(resolve, baseDelay + jitter))
    },
    lazy: true,
    keepAlive: 10_000,
    connectionAckWaitTimeout: 10_000,
    on: {
      connected: () => {
        wsEventListener?.({ type: 'connected' })
      },
      closed: (event: unknown) => {
        const closeEvent = event as { code?: number; reason?: string }
        console.warn(
          `[WebSocket] Connection closed (code: ${closeEvent?.code ?? 'none'}, reason: ${closeEvent?.reason || 'none'})`,
        )
        wsEventListener?.({ type: 'closed' })
      },
      error: () => {
        wsEventListener?.({ type: 'error' })
      },
    },
  })

  // Force immediate reconnection when the browser regains network connectivity.
  // Without this, the client waits for its current retry backoff timer to expire,
  // which can take up to a minute after a network change.
  window.addEventListener('online', () => {
    console.info('[WebSocket] Network online detected, forcing reconnect')
    wsClient.terminate()
  })

  const wsLink = new GraphQLWsLink(wsClient)

  return new ApolloClient({
    cache: new InMemoryCache({
      possibleTypes: introspectionResult.possibleTypes,
      typePolicies: TypePolicies,
      // Note(Filip): the "resultCacheMaxSize" prop causes heavy performance regression in the Stop Network
    }),
    connectToDevTools: true,
    name: appName,
    version: apolloVersion,
    link: from([
      retryLink,
      split(({ query }) => {
        const definition = getMainDefinition(query)

        return (
          definition.kind === 'OperationDefinition' &&
          definition.operation === 'subscription'
        )
      }, wsLink),
      httpLink,
    ]),
  })
}
